// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";

import { request } from "../api/client";
import { brand } from "../theme";
import { colors } from "../styles/colors";
import { cn } from "../utils/cn";
import { capitalizeFirstLetter } from "../utils/stringUtils";
import { APP_ROUTES } from "../utils/appRoutes";
import { API_URLS } from "../utils/urls";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { createDream, resetCreateDreamState } from "../store/dreamSlice";
import { fetchDreamCards } from "../store/dashboardSlice";
import { MAX_CATEGORIES } from "../store/settingsSlice";
import { CategoryDrawer } from "../components/CategoryDrawer";
import { UpgradeModal } from "../components/upgrade";
import { AIConsentDialog } from "../components/AIConsentDialog";
import { useIsPremium } from "../hooks/useIsPremium";
import { FREE_PLAN_LIMITS } from "../config/upgrade";
import { ReactComponent as IconCheckFilled } from "../assets/icons/check-circle.svg";
import { ReactComponent as IconCircle } from "../assets/icons/circle.svg";
import { ReactComponent as IconEdit } from "../assets/icons/pencil.svg";
import { ReactComponent as IconTrash } from "../assets/icons/trash.svg";
import { ReactComponent as IconPlus } from "../assets/icons/plus.svg";

const PRESET_KEYS = [
  "this_week",
  "this_month",
  "last_month",
  "this_year",
  "next_year",
];

const PRESET_LABELS = {
  this_week: "This week",
  this_month: "This month",
  last_month: "Last month",
  this_year: "This year",
  next_year: "Next year",
};

const DESCRIPTION_MAX_CHARS = 300;

type ClarifyQuestion = { id: string; question: string; options: string[] };

const MONTH_LABELS_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const DREAM_EXAMPLES = [
  {
    category: "Adventure",
    color: "#eff6ff",
    accent: "#3b82f6",
    title: "Travel the world",
    description: "I want to visit 30 countries before I turn 40, experience different cultures, try authentic local foods and create memories that last a lifetime.",
  },
  {
    category: "Career",
    color: "#fdf4ff",
    accent: "#a855f7",
    title: "Launch my own business",
    description: "I've always wanted to build a product that truly helps people. I want to leave my 9-5 and create something meaningful and sustainable from scratch.",
  },
  {
    category: "Creative",
    color: "#fff7ed",
    accent: "#f97316",
    title: "Write and publish a book",
    description: "I have a story inside me that's been waiting to come out. I want to write a novel that resonates with readers around the world.",
  },
  {
    category: "Health",
    color: "#f0fdf4",
    accent: "#22c55e",
    title: "Run a marathon",
    description: "I want to push my limits, feel stronger every day, and cross a finish line I once thought was impossible for someone like me.",
  },
  {
    category: "Lifestyle",
    color: "#fefce8",
    accent: "#eab308",
    title: "Buy my dream home",
    description: "I want a place I can truly call mine — a space to grow, to host family, and to build a life entirely on my own terms.",
  },
  {
    category: "Education",
    color: "#f0fdfa",
    accent: "#14b8a6",
    title: "Become fluent in Spanish",
    description: "I want to connect with people in their own language, open new career paths, and experience a whole new culture firsthand.",
  },
];

const MOTIVATIONAL_QUOTES = [
  { text: "The journey of a thousand miles begins with a single step.", author: "Lao Tzu" },
  { text: "You've already started — that's the hardest part.", author: "" },
  { text: "A goal without a plan is just a wish.", author: "Antoine de Saint-Exupéry" },
  { text: "Dream big. Start small. Act now.", author: "" },
  { text: "Every expert was once a beginner.", author: "" },
  { text: "The best time to start was yesterday. The next best time is now.", author: "" },
  { text: "Small steps every day lead to big changes.", author: "" },
  { text: "Your future self will thank you for starting today.", author: "" },
  { text: "Believe you can and you're halfway there.", author: "Theodore Roosevelt" },
  { text: "Progress, not perfection.", author: "" },
  { text: "The only impossible journey is the one you never begin.", author: "Tony Robbins" },
  { text: "Creating your personalised roadmap…", author: "" },
];

/* ─── Layout helpers ───────────────────────────────────────────────────── */

const DreamWizardBleed = ({ children, className = "" }) => (
  <div
    className={cn(
      "w-screen max-w-[100vw] bg-slate-50 [margin-inline:calc(50%-50vw)] overflow-x-clip",
      className,
    )}
    data-testid="dream-wizard-screen-bleed"
  >
    <div className="mx-auto w-full max-w-[1360px] px-4">{children}</div>
  </div>
);

const ProgressBar = ({ percent = 0, color = colors.success }) => (
  <div className="h-2 w-full overflow-hidden rounded-full bg-black/[0.04]">
    <div
      className="h-full rounded-full transition-all duration-300"
      style={{ width: `${Math.min(100, Math.max(0, percent))}%`, backgroundColor: color }}
    />
  </div>
);

const clampDescriptionToMaxChars = (value, maxChars) => {
  if (value == null || value === "") return "";
  if (value.length <= maxChars) return value;
  return value.slice(0, maxChars);
};

/* ─── Button class presets ─────────────────────────────────────────────── */

const btnPrimary =
  "inline-flex h-10 min-w-[92px] cursor-pointer items-center justify-center rounded-xl border border-brand-primary bg-brand-primary px-8 text-sm font-semibold text-white transition-colors hover:bg-brand-primaryDark disabled:cursor-not-allowed disabled:opacity-50";

const btnBack =
  "inline-flex h-10 min-w-[92px] cursor-pointer items-center justify-center rounded-xl border border-brand-primary bg-white px-8 text-sm font-semibold text-brand-primary transition-colors hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50";

/* ─── Main page ────────────────────────────────────────────────────────── */

export default function DreamCreate() {
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isCreating, createError, pendingPayload } = useAppSelector((state) => state.dream);
  const saveInFlight = useRef(false);
  const clarifyRequest = useRef<AbortController | null>(null);
  useEffect(() => () => clarifyRequest.current?.abort(), []);
  const { dreamCards } = useAppSelector((state) => state.dashboard);
  const isPremium = useIsPremium();
  const [showUpgradeGate, setShowUpgradeGate] = useState(false);
  const [showAIConsent, setShowAIConsent] = useState(false);
  const titleInputRef = useRef(null);
  const descriptionInputRef = useRef(null);

  const [step, setStep] = useState(0);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [clarifyAnswers, setClarifyAnswers] = useState<Record<string, string>>({});
  const [clarifyIndex, setClarifyIndex] = useState(0);
  const [clarifyCustom, setClarifyCustom] = useState("");
  const [clarifyQuestions, setClarifyQuestions] = useState<ClarifyQuestion[]>([]);
  const [clarifyLoading, setClarifyLoading] = useState(false);
  const [timelineHint, setTimelineHint] = useState<{ startMonth: number; startYear: number; endMonth: number; endYear: number } | null>(null);

  const handleDescriptionChange = useCallback((e) => {
    const next = clampDescriptionToMaxChars(e.target.value, DESCRIPTION_MAX_CHARS);
    setDescription(next);
    const el = e.target;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  const [startPreset, setStartPreset] = useState("this_month");
  const [targetPreset, setTargetPreset] = useState("this_year");
  const nowDate = useMemo(() => { const n = new Date(); return { month: n.getMonth() + 1, year: n.getFullYear() }; }, []);
  const [startCustom, setStartCustom] = useState({ month: nowDate.month, year: nowDate.year });
  const [targetCustom, setTargetCustom] = useState({ month: 12, year: nowDate.year });
  const { categories: settingsCategories } = useAppSelector((state) => state.settings);
  const [category, setCategory] = useState<string>("Personal");
  const [catDrawerOpen, setCatDrawerOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchDreamCards());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!settingsCategories.length) return;
    const exists = settingsCategories.some((c) => c.name === category);
    if (!exists) setCategory(settingsCategories[0].name);
  }, [settingsCategories]);

  const dreamColor = useMemo(() => {
    const cat = settingsCategories.find((c) => c.name === category);
    return cat?.color ?? "#3B82F6";
  }, [category, settingsCategories]);

  const [mode, setMode] = useState<"ai" | "manual" | "vendor">("ai");
  const [vendorDrafts, setVendorDrafts] = useState([]);
  const [selectedVendorDraft, setSelectedVendorDraft] = useState(null);
  const [vendorLoading, setVendorLoading] = useState(false);

  useEffect(() => {
    if (step !== "vendor") return;
    if (vendorDrafts.length > 0) return;
    let cancelled = false;
    setVendorLoading(true);
    (async () => {
      try {
        const token = await getAccessTokenSilently({ cacheMode: "on" });
        const data = await request("/api/vendor/match", {
          method: "POST",
          auth: true,
          getAccessToken: () => Promise.resolve(token),
          body: { title: title.trim(), description: (description || "").trim() },
        });
        if (!cancelled) setVendorDrafts(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) showToast("Failed to load vendor plans", "error");
      } finally {
        if (!cancelled) setVendorLoading(false);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [draft, setDraft] = useState(null);
  const [plan, setPlan] = useState({ goals: [] });
  
  const [editing, setEditing] = useState({ g: null, m: null });

  /* Toast state */
  const [exampleIdx, setExampleIdx] = useState(0);
  const [exampleVisible, setExampleVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setExampleVisible(false);
      setTimeout(() => {
        setExampleIdx((i) => (i + 1) % DREAM_EXAMPLES.length);
        setExampleVisible(true);
      }, 350);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);
  const showToast = useCallback((text, type = "success") => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ text, type });
    toastTimerRef.current = setTimeout(() => setToast(null), 4000);
  }, []);

  const toastEl = toast ? (
    <div
      role="alert"
      className={cn(
        "fixed left-1/2 top-6 z-50 -translate-x-1/2 rounded-lg border px-5 py-3 text-sm font-medium shadow-lg",
        toast.type === "error"
          ? "border-red-200 bg-red-50 text-red-700"
          : "border-green-200 bg-green-50 text-green-700",
      )}
    >
      {toast.text}
    </div>
  ) : null;

  const canGoNextFromStep0 = useMemo(
    () => title.trim().length > 0 && description.trim().length > 0,
    [title, description],
  );
  const formattedDreamTitle = useMemo(
    () => capitalizeFirstLetter(title.trim()),
    [title],
  );

  const monthYearFromPreset = (presetKey, kind) => {
    const now = new Date();
    const curMonth = now.getMonth() + 1;
    const curYear = now.getFullYear();

    if (presetKey === "this_week") return { month: curMonth, year: curYear };
    if (presetKey === "this_month") return { month: curMonth, year: curYear };
    if (presetKey === "last_month") {
      const d = new Date(curYear, curMonth - 2, 1);
      return { month: d.getMonth() + 1, year: d.getFullYear() };
    }
    if (presetKey === "this_year") {
      if (kind === "start") return { month: 1, year: curYear };
      return { month: 12, year: curYear };
    }
    if (presetKey === "next_year") {
      if (kind === "start") return { month: 1, year: curYear + 1 };
      return { month: 12, year: curYear + 1 };
    }
    return { month: curMonth, year: curYear };
  };

  const selectPillCls = (active) =>
    cn(
      "h-[34px] cursor-pointer rounded-[10px] border px-3.5 text-sm font-normal text-text-wizard shadow-none transition-colors",
      active
        ? "border-brand-primary bg-surface-softBlue"
        : "border-black/[0.08] bg-white",
    );

  const milestonePillCls = (done) =>
    cn(
      "flex w-full cursor-pointer items-center justify-between gap-2.5 rounded border px-3.5 py-2 text-left text-sm text-text-wizard transition-colors",
      done ? "border-black/[0.06] bg-surface-softBlue" : "border-black/[0.06] bg-surface-softGray",
    );

  const isGoalComplete = (g) => {
    const ms = g?.milestones || [];
    if (!ms.length) return false;
    return ms.every((m) => !!m?.done);
  };

  const completedGoalsCount = useMemo(() => {
    const goals = plan.goals || [];
    const done = goals.filter((g) => isGoalComplete(g)).length;
    const total = goals.length;
    return { done, total };
  }, [plan]);

  const handleToggleGoalComplete = (gIdx) => {
    setPlan((prev) => {
      const goals = (prev.goals || []).slice();
      const g = goals[gIdx];
      if (!g) return prev;
      const ms = (g.milestones || []).slice();
      if (!ms.length) return prev;
      const currentlyComplete = ms.every((m) => !!m?.done);
      const nextMs = ms.map((m) => ({ ...m, done: !currentlyComplete }));
      goals[gIdx] = { ...g, milestones: nextMs };
      return { ...prev, goals };
    });
  };

  const startMY = useMemo(
    () => startPreset === "custom" ? startCustom : monthYearFromPreset(startPreset, "start"),
    [startPreset, startCustom],
  );
  const targetMY = useMemo(
    () => targetPreset === "custom" ? targetCustom : monthYearFromPreset(targetPreset, "target"),
    [targetPreset, targetCustom],
  );
  const isDateOrderValid = useMemo(
    () => (targetMY.year * 12 + targetMY.month) >= (startMY.year * 12 + startMY.month),
    [startMY, targetMY],
  );

  const [quoteIdx, setQuoteIdx] = useState(0);
  const [quoteVisible, setQuoteVisible] = useState(true);

  useEffect(() => {
    if (step !== 2) return;
    const interval = setInterval(() => {
      setQuoteVisible(false);
      setTimeout(() => {
        setQuoteIdx((i) => (i + 1) % MOTIVATIONAL_QUOTES.length);
        setQuoteVisible(true);
      }, 400);
    }, 3500);
    return () => clearInterval(interval);
  }, [step]);

  useEffect(() => {
    let mounted = true;
    const controller = new AbortController();

    if (step !== 2) return () => {};
    if (draft) return () => {};

    (async () => {
      setIsGenerating(true);

      try {
        const clarifyQA = clarifyQuestions
          .filter((q) => clarifyAnswers[q.id])
          .map((q) => ({ question: q.question, answer: clarifyAnswers[q.id] }));

        const data = await request(API_URLS.agentDreamPlan, {
          method: "POST",
          signal: controller.signal,
          timeout: 110000,
          auth: true,
          getAccessToken: () => getAccessTokenSilently({ cacheMode: "on" }),
          body: {
            title: title.trim(),
            description: (description || "").trim(),
            category: category || undefined,
            startMonth: startMY.month,
            startYear: startMY.year,
            targetMonth: targetMY.month,
            targetYear: targetMY.year,
            clarifyQA: clarifyQA.length ? clarifyQA : undefined,
          },
        });

        if (!mounted) return;

        const goals = Array.isArray(data?.goals) ? data.goals : [];
        if (!goals.length || goals.some((goal) => typeof goal?.title !== "string" || !goal.title.trim() || !Array.isArray(goal.milestones))) {
          throw new Error("The AI plan could not be verified. Retry or choose manual planning.");
        }
        const out = { goals };
        setDraft(out);
        setPlan({
          goals: goals.map((g) => ({
            title: g?.title || "Goal",
            description: g?.description || "",
            startMonth: g?.startMonth,
            startYear: g?.startYear,
            endMonth: g?.endMonth,
            endYear: g?.endYear,
            milestones: (Array.isArray(g?.milestones) ? g.milestones : []).map(
              (m) => ({
                title: m?.title || "",
                description: m?.description || "",
                date: m?.date || "",
                done: false,
              }),
            ),
          })),
        });
      } catch (e) {
        if (!mounted) return;
        showToast(e?.message || "Failed to generate dream plan", "error");
        setStep(1);
      } finally {
        if (!mounted) return;
        setIsGenerating(false);
      }
    })();

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [
    step,
    draft,
    title,
    description,
    category,
    clarifyQuestions,
    clarifyAnswers,
    startMY.month,
    startMY.year,
    targetMY.month,
    targetMY.year,
    getAccessTokenSilently,
    showToast,
  ]);

  useEffect(() => {
    if (step !== 1 || !timelineHint) return;
    setStartPreset("custom");
    setStartCustom({ month: timelineHint.startMonth, year: timelineHint.startYear });
    setTargetPreset("custom");
    setTargetCustom({ month: timelineHint.endMonth, year: timelineHint.endYear });
  }, [step, timelineHint]);

  useEffect(() => {
    if (step !== 0) return;
    const id = requestAnimationFrame(() => {
      titleInputRef.current?.focus?.({ preventScroll: true });
    });
    return () => cancelAnimationFrame(id);
  }, [step]);

  const buildVendorGoals = (draft) => {
    const startBase = startMY.year * 12 + startMY.month - 1;
    const endBase = targetMY.year * 12 + targetMY.month - 1;
    const dreamDur = Math.max(1, endBase - startBase);
    const draftDur = Math.max(1, draft.durationMonths || 1);
    return (draft.goals || []).map((g) => {
      const sM = startBase + Math.round((g.startOffset / draftDur) * dreamDur);
      const eM = startBase + Math.round((g.endOffset / draftDur) * dreamDur);
      return {
        title: g.title,
        description: g.description || "",
        completed: false,
        startMonth: (sM % 12) + 1,
        startYear: Math.floor(sM / 12),
        endMonth: (eM % 12) + 1,
        endYear: Math.floor(eM / 12),
        milestones: (g.milestones || []).map((m) => {
          const dM = startBase + Math.round((m.dateOffset / draftDur) * dreamDur);
          const y = Math.floor(dM / 12);
          const mo = (dM % 12) + 1;
          return { title: m.title, description: "", date: `${y}-${String(mo).padStart(2, "0")}-15`, completed: false };
        }),
      };
    });
  };

  const saveDreamAndNavigate = async (goalsPayload) => {
    if (saveInFlight.current) return;
    saveInFlight.current = true;
    try {
      setIsSaving(true);
      const result = await dispatch(
        createDream({
          payload: {
            dream: {
              title: title.trim(),
              description: (description || "").trim(),
              startMonth: startMY.month,
              startYear: startMY.year,
              targetMonth: targetMY.month,
              targetYear: targetMY.year,
              color: dreamColor,
              category,
            },
            goals: goalsPayload,
          },
          getAccessToken: () => getAccessTokenSilently({ cacheMode: "on" }),
        }),
      ).unwrap();
      const createdId = result?.dream?.id;
      dispatch(resetCreateDreamState());
      if (createdId) navigate(APP_ROUTES.dreamDetail(createdId));
      else navigate(APP_ROUTES.dashboard);
    } catch (e) {
      setIsSaving(false);
      showToast("Save confirmation was interrupted. Retry the same submitted plan, or refresh My Dreams to check whether it completed.", "error");
    } finally {
      saveInFlight.current = false;
    }
  };

  const proceedWithMode = useCallback((newMode: string, consentConfirmed = false) => {
    if (!canGoNextFromStep0) {
      showToast("Please enter a title and description", "error");
      return;
    }
    if (!isPremium && dreamCards.length >= FREE_PLAN_LIMITS.dreams) {
      setShowUpgradeGate(true);
      return;
    }
    if (newMode === "ai" && !consentConfirmed) { setShowAIConsent(true); return; }
    clarifyRequest.current?.abort();
    setMode(newMode);
    if (newMode === "vendor") { setStep("vendor"); return; }
    if (newMode === "manual") { setPlan({ goals: [] }); setStep(1); return; }
    // AI mode: fetch contextual questions, then go to clarify step
    setClarifyAnswers({});
    setClarifyIndex(0);
    setClarifyCustom("");
    setClarifyLoading(true);
    setStep("clarify");
    const controller = new AbortController();
    clarifyRequest.current = controller;
    request(API_URLS.agentClarifyQuestions, {
      method: "POST",
      signal: controller.signal,
      timeout: 110000,
      auth: true,
      getAccessToken: () => getAccessTokenSilently({ cacheMode: "on" }),
      body: { title: title.trim(), description: (description || "").trim(), category: category || undefined },
    })
      .then((data) => {
        if (controller.signal.aborted) return;
        if (!Array.isArray(data?.questions)) throw new Error("AI questions could not be verified.");
        if (data?.timelineHint) setTimelineHint(data.timelineHint);
        if (Array.isArray(data?.questions) && data.questions.length > 0) {
          setClarifyQuestions(data.questions);
        } else {
          // AI decided no clarification needed — skip straight to timeline
          setStep(1);
        }
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        if (error?.httpStatus === 403 || error?.httpStatus === 423) {
          showToast(error.message || "AI sharing permission is required.", "error");
          setStep(0);
          return;
        }
        // On error, require an explicit retry or manual planning choice.
        showToast("AI questions could not be loaded. Retry or choose manual planning.", "error");
        setStep(0);
      })
      .finally(() => { if (!controller.signal.aborted) setClarifyLoading(false); });
  }, [canGoNextFromStep0, isPremium, dreamCards.length, title, description, category, getAccessTokenSilently, showToast]);

  const handleWizardNext = useCallback(async () => {
    if (step === 0) {
      if (!canGoNextFromStep0) {
        showToast("Please enter a title and description", "error");
        return;
      }
      if (!isPremium && dreamCards.length >= FREE_PLAN_LIMITS.dreams) {
        setShowUpgradeGate(true);
        return;
      }
      if (mode === "vendor") { setStep("vendor"); return; }
      if (mode === "ai") { proceedWithMode("ai"); return; }
      // All other modes: go to date/category selection first
      if (mode === "manual") setPlan({ goals: [] });
      setStep(1);
      return;
    }
    if (step === "vendor") {
      if (!selectedVendorDraft) { showToast("Please select a vendor plan", "error"); return; }
      setStep(1);
      return;
    }
    if (step === "plan") {
      setStep(1);
      return;
    }
    if (step === 1) {
      if (!isDateOrderValid) return;
      if (mode === "vendor" && selectedVendorDraft) {
        await saveDreamAndNavigate(buildVendorGoals(selectedVendorDraft));
        return;
      }
      if (mode === "manual") {
        await saveDreamAndNavigate([]);
        return;
      }
      // AI mode — dates are now set, proceed to generation
      setStep(2);
      return;
    }
    if (step === 2) {
      // Called automatically after AI generation completes
      if (isGenerating || !draft || isSaving) return;
      const goals = (plan.goals || []).map((g) => ({
        title: (g.title || "").trim() || "Goal",
        description: g.description || "",
        completed: false,
        startMonth: g.startMonth,
        startYear: g.startYear,
        endMonth: g.endMonth,
        endYear: g.endYear,
        milestones: (g.milestones || [])
          .map((m) => ({ title: (m.title || "").trim(), description: m.description || "", date: m.date || "", completed: false }))
          .filter((m) => m.title),
      }));
      await saveDreamAndNavigate(goals);
      return;
    }
  }, [step, mode, canGoNextFromStep0, isDateOrderValid, selectedVendorDraft, plan, isGenerating, isSaving, showToast, buildVendorGoals, saveDreamAndNavigate, proceedWithMode, isPremium, dreamCards.length]);

  useEffect(() => {
    if (step !== 2) return;
    if (isGenerating) return;
    if (!draft) return;
    if (isSaving || createError) return;
    const timer = setTimeout(() => handleWizardNext(), 1200);
    return () => clearTimeout(timer);
  }, [step, isGenerating, isSaving, createError, draft, handleWizardNext]);

  const handleDescriptionKeyDown = useCallback(
    (e) => {
      if (e.key !== "Enter" || e.shiftKey) return;
      e.preventDefault();
      handleWizardNext();
    },
    [handleWizardNext],
  );

  useEffect(() => {
    if (step !== 2 && step !== 3) return;
    const handleKeyDown = (e) => {
      if (e.key !== "Enter" || e.repeat) return;
      const el = e.target;
      if (!el || typeof el.tagName !== "string") return;
      const tag = el.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "BUTTON" || tag === "A" || tag === "SELECT") return;
      if (el.isContentEditable) return;
      e.preventDefault();
      handleWizardNext();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [step, handleWizardNext]);

  const handleWizardBack = () => {
    if (step === 0) return;
    if (step === 2) { setDraft(null); setPlan({ goals: [] }); setStep(1); return; }
    if (step === "clarify") { setStep(0); return; }
    if (step === "vendor") { setStep(0); return; }
    if (step === "plan") { setStep(0); return; }
    if (step === 1 && mode === "vendor") { setStep("vendor"); return; }
    if (step === 1 && mode === "ai") { setStep("clarify"); return; }
    if (step === 1) { setStep(0); return; }
    setStep((s) => typeof s === "number" ? Math.max(0, s - 1) : 0);
  };

  const toggleMilestone = (gIdx, mIdx) => {
    setPlan((prev) => {
      const goals = (prev.goals || []).slice();
      const g = goals[gIdx];
      if (!g) return prev;
      const ms = (g.milestones || []).slice();
      const m = ms[mIdx];
      if (!m) return prev;
      ms[mIdx] = { ...m, done: !m.done };
      goals[gIdx] = { ...g, milestones: ms };
      return { ...prev, goals };
    });
  };

  const handleRemoveGoal = (gIdx) => {
    setPlan((prev) => {
      const goals = (prev.goals || []).slice();
      if (!goals[gIdx]) return prev;
      goals.splice(gIdx, 1);
      return { ...prev, goals };
    });
  };

  const handleAddMilestone = (gIdx) => {
    setPlan((prev) => {
      const goals = (prev.goals || []).slice();
      const g = goals[gIdx];
      if (!g) return prev;
      const ms = (g.milestones || []).slice();
      const newIndex = ms.length;
      ms.push({ title: "", description: "", date: "", done: false });
      goals[gIdx] = { ...g, milestones: ms };
      setEditing({ g: gIdx, m: newIndex });
      return { ...prev, goals };
    });
  };

  const handleRemoveMilestone = (gIdx, mIdx) => {
    setPlan((prev) => {
      const goals = (prev.goals || []).slice();
      const g = goals[gIdx];
      if (!g) return prev;
      const ms = (g.milestones || []).slice();
      if (!ms[mIdx]) return prev;
      ms.splice(mIdx, 1);
      goals[gIdx] = { ...g, milestones: ms };
      return { ...prev, goals };
    });
    setEditing({ g: null, m: null });
  };

  const handleMilestoneTitleChange = (gIdx, mIdx, nextTitle) => {
    setPlan((prev) => {
      const goals = (prev.goals || []).slice();
      const g = goals[gIdx];
      if (!g) return prev;
      const ms = (g.milestones || []).slice();
      const m = ms[mIdx];
      if (!m) return prev;
      ms[mIdx] = { ...m, title: nextTitle };
      goals[gIdx] = { ...g, milestones: ms };
      return { ...prev, goals };
    });
  };

  const handleGoalTitleChange = (gIdx, nextTitle) => {
    setPlan((prev) => {
      const goals = (prev.goals || []).slice();
      if (!goals[gIdx]) return prev;
      goals[gIdx] = { ...goals[gIdx], title: nextTitle };
      return { ...prev, goals };
    });
  };

  const handleAddGoal = () => {
    setPlan((prev) => ({
      ...prev,
      goals: [
        ...(prev.goals || []),
        { title: "Goal", description: "", milestones: [] },
      ],
    }));
  };

  const handleCreateDream = async () => {
    const payloadGoals = (plan.goals || [])
      .map((g) => ({
        title: (g.title || "").trim() || "Goal",
        description: g.description || "",
        completed: isGoalComplete(g),
        milestones: (g.milestones || [])
          .map((m) => ({
            title: (m.title || "").trim(),
            description: m.description || "",
            date: m.date || "",
            completed: !!m.done,
          }))
          .filter((m) => m.title),
      }))
      .filter((g) => (g.milestones || []).length > 0);

    try {
      const result = await dispatch(
        createDream({
          payload: {
            dream: {
              title: title.trim(),
              description: (description || "").trim(),
              startMonth: startMY.month,
              startYear: startMY.year,
              targetMonth: targetMY.month,
              targetYear: targetMY.year,
              color: dreamColor,
              category: category,
            },
            goals: payloadGoals,
          },
          getAccessToken: () => getAccessTokenSilently({ cacheMode: "on" }),
        }),
      ).unwrap();

      const createdId = result?.dream?.id;
      showToast("Dream created");
      dispatch(resetCreateDreamState());
      if (createdId) navigate(APP_ROUTES.dreamDetail(createdId));
      else navigate(APP_ROUTES.dashboard);
    } catch (error) {
      showToast(error || "Failed to create dream", "error");
    }
  };

  const handleTitleChange = (e) => {
    setTitle(e.target.value);
  };

  const handleTitleKeyDown = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    descriptionInputRef.current?.focus?.();
  };

  const handleSelectStartPreset = (presetKey) => {
    setStartPreset(presetKey);
  };

  const handleSelectTargetPreset = (presetKey) => {
    setTargetPreset(presetKey);
  };

  /* ── Step 0 – Title + Description ────────────────────────────────── */

  if (step === 0) {
    const ex = DREAM_EXAMPLES[exampleIdx];
    return (
      <DreamWizardBleed>
        {toastEl}
        <AIConsentDialog open={showAIConsent} onCancel={() => setShowAIConsent(false)} onAccepted={() => {
          setShowAIConsent(false);
          proceedWithMode("ai", true);
        }} />
        <div
          data-testid="dream-wizard-step-0"
          className="box-border flex min-h-[calc(100svh-186px)] items-start overflow-y-auto px-4 pb-10 pt-6 md:items-center md:px-20 md:pb-[72px] md:pt-0"
        >
          {/* Left – form */}
          <div className="flex w-full flex-1 flex-col justify-center md:w-auto" style={{ maxWidth: 520 }}>
            <p className="mb-2 text-xs font-bold uppercase tracking-widest" style={{ color: "#1677FF" }}>
              New Dream
            </p>
            <h1 className="mb-4 text-3xl font-bold" style={{ color: "#0b1b3a" }}>
              What's been on your mind lately?
            </h1>

            {/* Category selection — pill style like dashboard */}
            <div className="mb-7 flex flex-wrap items-center gap-1.5">
              {settingsCategories.map((cat) => (
                <button
                  key={cat.name}
                  type="button"
                  onClick={() => setCategory(cat.name)}
                  className={cn(
                    "rounded-full border-0 px-3 py-1 text-xs font-semibold uppercase tracking-wide transition-all",
                    category === cat.name
                      ? "text-white shadow-sm"
                      : "bg-slate-100 text-slate-500 hover:bg-slate-200",
                  )}
                  style={category === cat.name ? { background: cat.color } : undefined}
                >
                  {cat.name}
                </button>
              ))}
              {/* Add category button */}
              {settingsCategories.length < (isPremium ? MAX_CATEGORIES : FREE_PLAN_LIMITS.categories) && (
                <button
                  type="button"
                  onClick={() => setCatDrawerOpen(true)}
                  className="rounded-full border border-dashed border-slate-300 px-3 py-1 text-xs font-medium text-slate-400 transition-colors hover:border-brand-primary/40 hover:text-brand-primary"
                >
                  + Add
                </button>
              )}
            </div>

            <CategoryDrawer
              open={catDrawerOpen}
              mode="add"
              onClose={() => setCatDrawerOpen(false)}
              onSaved={(savedName) => setCategory(savedName)}
            />

            <div className="flex flex-col gap-4">
              {/* Title */}
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Dream title <span className="text-red-400">*</span>
                </label>
                <input
                  ref={titleInputRef}
                  data-testid="dream-title-input"
                  autoFocus
                  type="text"
                  value={title}
                  onChange={handleTitleChange}
                  onKeyDown={handleTitleKeyDown}
                  placeholder="e.g. Travel the world, Launch my business…"
                  className="ds-input"
                />
              </div>

              {/* Description – now required */}
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Tell us more <span className="text-red-400">*</span>
                </label>
                <textarea
                  ref={descriptionInputRef}
                  data-testid="dream-description-input"
                  value={description}
                  onChange={handleDescriptionChange}
                  onKeyDown={handleDescriptionKeyDown}
                  placeholder="Briefly describe what this dream means to you…"
                  rows={4}
                  className="ds-input resize-none leading-relaxed"
                />
                <p className="mt-1 text-right text-xs text-slate-400">
                  {description.length} / {DESCRIPTION_MAX_CHARS}
                </p>
              </div>
            </div>

            {/* Action buttons */}
            <div className="mt-8 space-y-2.5">
              <div className="flex gap-2">
                <button
                  type="button"
                  className={btnPrimary + " relative flex-1 flex items-center justify-center gap-2"}
                  disabled={!canGoNextFromStep0}
                  onClick={() => proceedWithMode("ai")}
                  data-testid="dream-wizard-next-button"
                >
                  Create AI Plan
                  {!isPremium && dreamCards.length >= FREE_PLAN_LIMITS.dreams && (
                    <span className="inline-flex items-center gap-1 rounded-md bg-white/20 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide">
                      <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                        <rect x="1" y="3.5" width="6" height="4.2" rx="1" stroke="currentColor" strokeWidth="1.1"/>
                        <path d="M2.5 3.5V2.5a1.5 1.5 0 1 1 3 0v1" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round"/>
                      </svg>
                      Premium
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  className={btnBack + " relative flex-1 flex items-center justify-center gap-2"}
                  disabled={!canGoNextFromStep0}
                  onClick={() => proceedWithMode("manual")}
                >
                  Plan it Myself
                  {!isPremium && dreamCards.length >= FREE_PLAN_LIMITS.dreams && (
                    <span className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide" style={{ background: "rgba(0,136,255,0.1)", color: "#0088FF" }}>
                      <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                        <rect x="1" y="3.5" width="6" height="4.2" rx="1" stroke="currentColor" strokeWidth="1.1"/>
                        <path d="M2.5 3.5V2.5a1.5 1.5 0 1 1 3 0v1" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round"/>
                      </svg>
                      Premium
                    </span>
                  )}
                </button>
              </div>
              {!isPremium && dreamCards.length >= FREE_PLAN_LIMITS.dreams && (
                <p className="m-0 text-center text-[12px] font-medium" style={{ color: "#92400e" }}>
                  You have reached the free plan limit of 1 dream. Upgrade to Premium for unlimited dreams.
                </p>
              )}
            </div>

          </div>

          {/* Right – rotating example card */}
          <div className="hidden lg:flex flex-1 items-center justify-end pl-12">
            <div
              style={{
                opacity: exampleVisible ? 1 : 0,
                transform: exampleVisible ? "translateY(0)" : "translateY(12px)",
                transition: "opacity 0.35s ease, transform 0.35s ease",
                width: "100%",
                maxWidth: 380,
              }}
            >
              {/* Inspiration label */}
              <div className="mb-4 flex items-center gap-2">
                <div className="h-px flex-1 bg-slate-200" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Dream inspiration</span>
                <div className="h-px flex-1 bg-slate-200" />
              </div>

              {/* Card */}
              <div className="rounded-2xl bg-white p-6">
                <div className="mb-3 flex items-center gap-2">
                  <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-bold text-slate-500">
                    {ex.category}
                  </span>
                </div>
                <p className="mb-2 text-lg font-bold" style={{ color: "#0b1b3a" }}>
                  {ex.title}
                </p>
                <p className="text-sm leading-relaxed text-slate-500">
                  {ex.description}
                </p>
              </div>

              {/* Dot indicators */}
              <div className="mt-4 flex justify-center gap-1.5">
                {DREAM_EXAMPLES.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => { setExampleVisible(false); setTimeout(() => { setExampleIdx(i); setExampleVisible(true); }, 350); }}
                    className="h-1.5 rounded-full transition-all duration-300"
                    style={{
                      width: i === exampleIdx ? 20 : 6,
                      background: i === exampleIdx ? "#0088FF" : "#cbd5e1",
                    }}
                    aria-label={`Show example ${i + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </DreamWizardBleed>
    );
  }

  /* ── Clarifying questions step (AI mode only) ─────────────────── */

  if (step === "clarify") {
    const questions = clarifyQuestions;
    const q = questions[clarifyIndex];
    const totalQ = questions.length;

    const commitAnswer = (answer: string) => {
      const next = { ...clarifyAnswers, [q.id]: answer };
      setClarifyAnswers(next);
      setClarifyCustom("");
      if (clarifyIndex + 1 < totalQ) {
        setClarifyIndex(clarifyIndex + 1);
      } else {
        // All questions answered — go to timeline step
        setStep(1);
      }
    };

    const handleSkipAll = () => {
      setStep(1);
    };

    const handleCustomSubmit = () => {
      const val = clarifyCustom.trim();
      if (!val) return;
      commitAnswer(val);
    };

    if (clarifyLoading || !q) {
      return (
        <DreamWizardBleed>
          {toastEl}
          <div className="box-border flex min-h-[calc(100svh-186px)] items-center justify-center px-4">
            <div className="flex w-full max-w-[520px] flex-col gap-4">
              <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#0088FF" }}>
                Help us personalise your plan
              </p>
              <div style={{ height: 28, borderRadius: 8, background: "#e2e8f0", width: "70%", animation: "pulse 1.5s ease-in-out infinite" }} />
              <div className="flex flex-wrap gap-2.5 mt-2">
                {[120, 90, 140, 100].map((w, i) => (
                  <div key={i} style={{ height: 40, width: w, borderRadius: 99, background: "#e2e8f0", animation: "pulse 1.5s ease-in-out infinite" }} />
                ))}
              </div>
              <p style={{ fontSize: 13, color: "#94a3b8", marginTop: 8 }}>Generating questions for your dream...</p>
              <button
                type="button"
                onClick={() => setStep(1)}
                style={{ fontSize: 13, color: "#94a3b8", background: "none", border: "none", cursor: "pointer", textDecoration: "underline", alignSelf: "flex-start", marginTop: 16 }}
              />
            </div>
          </div>
        </DreamWizardBleed>
      );
    }

    return (
      <DreamWizardBleed>
        {toastEl}
        <div className="box-border flex min-h-[calc(100svh-186px)] items-center justify-center px-4 pb-10 pt-6 md:px-20 md:pb-[72px]">
          <div className="flex w-full max-w-[520px] flex-col">

            {/* Progress dots */}
            <div className="mb-8 flex items-center gap-2">
              {(clarifyQuestions.length ? clarifyQuestions : Array(3)).map((_, i) => (
                <div
                  key={i}
                  style={{
                    width: i === clarifyIndex ? 20 : 8,
                    height: 8,
                    borderRadius: 99,
                    background: i < clarifyIndex ? "#22c55e" : i === clarifyIndex ? "#0088FF" : "#e2e8f0",
                    transition: "all 0.3s ease",
                  }}
                />
              ))}
              <span style={{ marginLeft: "auto", fontSize: 12, color: "#94a3b8" }}>
                {clarifyIndex + 1} of {totalQ}
              </span>
            </div>

            {/* Question */}
            <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#0088FF", marginBottom: 8 }}>
              Help us personalise your plan
            </p>
            <h2 style={{ fontSize: 24, fontWeight: 700, color: "#0b1b3a", marginBottom: 24, lineHeight: 1.3 }}>
              {q.question}
            </h2>

            {/* Quick-select chips */}
            <div className="mb-5 flex flex-wrap gap-2.5">
              {q.options.map((opt) => {
                const isSelected = clarifyAnswers[q.id] === opt;
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => commitAnswer(opt)}
                    style={{
                      padding: "10px 18px",
                      borderRadius: 99,
                      fontSize: 14,
                      fontWeight: 500,
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                      border: isSelected ? "2px solid #0088FF" : "2px solid #e2e8f0",
                      background: isSelected ? "rgba(0,136,255,0.08)" : "#ffffff",
                      color: isSelected ? "#0088FF" : "#334155",
                    }}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>

            {/* Divider */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
              <div style={{ flex: 1, height: 1, background: "#e2e8f0" }} />
              <span style={{ fontSize: 12, color: "#94a3b8", whiteSpace: "nowrap" }}>or type your own</span>
              <div style={{ flex: 1, height: 1, background: "#e2e8f0" }} />
            </div>

            {/* Free-text input */}
            <div style={{ display: "flex", gap: 8, marginBottom: 32 }}>
              <input
                type="text"
                value={clarifyCustom}
                onChange={(e) => setClarifyCustom(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleCustomSubmit(); }}
                placeholder="Describe your situation..."
                className="ds-input"
                style={{ flex: 1 }}
              />
              <button
                type="button"
                onClick={handleCustomSubmit}
                disabled={!clarifyCustom.trim()}
                className={btnPrimary}
                style={{ minWidth: "auto", paddingInline: 20 }}
              >
                Next
              </button>
            </div>

            {/* Footer nav */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <button type="button" className={btnBack} onClick={handleWizardBack}>
                Back
              </button>
              <button
                type="button"
                onClick={handleSkipAll}
                style={{ fontSize: 13, color: "#94a3b8", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}
              />
            </div>

          </div>
        </div>
      </DreamWizardBleed>
    );
  }

  /* ── Vendor selection step ────────────────────────────────────── */

  if (step === "vendor") {
    return (
      <DreamWizardBleed>
        {toastEl}
        <div
          data-testid="dream-wizard-step-vendor"
          className="box-border min-h-[calc(100svh-186px)] overflow-y-auto px-6 pb-[72px] pt-6 md:px-20"
        >
          <p className="mb-1 text-xs font-bold uppercase tracking-widest" style={{ color: "#a855f7" }}>Vendor Plans</p>
          <h2 className="mb-1 text-2xl font-bold text-text-wizard">Expert plans for your dream</h2>
          <p className="mb-6 text-sm text-slate-500">We matched the best pre-built packages for “{formattedDreamTitle}”. Select one to continue.</p>

          {vendorLoading ? (
            <div className="flex items-center gap-3 py-12 text-slate-400">
              <svg className="h-5 w-5 animate-spin text-brand-primary" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
              <span className="text-sm font-medium">Finding the best plans for you…</span>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {vendorDrafts.map((d) => {
                const isSelected = selectedVendorDraft?.id === d.id;
                return (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => setSelectedVendorDraft(isSelected ? null : d)}
                    className="cursor-pointer rounded-2xl border p-5 text-left transition-all hover:shadow-md"
                    style={{
                      borderColor: isSelected ? "#a855f7" : "rgba(0,0,0,0.08)",
                      background: isSelected ? "#fdf4ff" : "#fff",
                      boxShadow: isSelected ? "0 0 0 2px #a855f730" : undefined,
                    }}
                  >
                    <div className="mb-3 flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{d.vendorEmoji}</span>
                        <div>
                          <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{d.vendor}</p>
                          <p className="text-sm font-bold text-slate-800">{d.name}</p>
                        </div>
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="text-base font-bold" style={{ color: "#a855f7" }}>${d.price}</p>
                        <p className="text-[10px] text-slate-400">{d.currency}</p>
                      </div>
                    </div>
                    <p className="mb-3 text-xs leading-relaxed text-slate-500">{d.tagline}</p>
                    <div className="flex items-center justify-between">
                      <span
                        className="rounded-full px-2 py-0.5 text-[11px] font-semibold"
                        style={{ background: "#a855f714", color: "#a855f7" }}
                      >
                        {d.category}
                      </span>
                      <span className="text-[11px] text-slate-400">
                        ⏱ {d.durationMonths} months &bull; ★ {d.rating} ({d.reviews})
                      </span>
                    </div>
                    {isSelected && (
                      <div className="mt-3 border-t border-purple-100 pt-3">
                        <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-purple-500">What&apos;s included</p>
                        <ul className="space-y-1">
                          {(d.goals || []).map((g, i) => (
                            <li key={i} className="flex items-start gap-1.5 text-xs text-slate-600">
                              <span className="mt-0.5 text-purple-400">✓</span> {g.title}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          <div className="mt-8 flex items-center gap-3">
            <button type="button" className={btnBack} onClick={handleWizardBack}>
              Back
            </button>
            <button
              type="button"
              className={btnPrimary}
              disabled={!selectedVendorDraft || vendorLoading}
              onClick={handleWizardNext}
              data-testid="dream-wizard-next-button"
            >
              Use this plan →
            </button>
          </div>
        </div>
      </DreamWizardBleed>
    );
  }

  /* ── Plan step – no longer used; goals/milestones are added in DreamDetail ── */

  if (step === "plan") {
    const goals = plan.goals || [];
    const isAI = mode === "ai";
    return (
      <DreamWizardBleed>
        {toastEl}
        <div
          data-testid="dream-wizard-step-plan"
          className="box-border min-h-[calc(100svh-186px)] overflow-y-auto pt-6 pb-12 px-6 md:px-20"
        >
          <div className="max-w-[720px]">
            <h2 className="mb-1 text-2xl font-bold text-text-wizard">{formattedDreamTitle}</h2>
            {description?.trim() ? (
              <p className="mb-4 text-base text-text-wizard/70">{description.trim()}</p>
            ) : null}

            <p className="text-sm text-black/45">
              {isAI ? "You may be further along than you think" : "Build your plan below — add goals and milestones at your own pace."}
            </p>

            <div className="mt-3 flex items-center justify-between">
              <div>
                <p className="text-sm font-bold text-text-wizard">
                  {goals.length === 0 ? "No goals yet" : `${goals.length} Goal${goals.length === 1 ? "" : "s"}`}
                </p>
                <p className="text-xs text-black/45">
                  {isAI ? "These are AI suggestions. You can remove or edit anything." : "You can add goals and milestones now or on the dream page later."}
                </p>
              </div>
              <button
                type="button"
                className="inline-flex cursor-pointer items-center gap-1.5 rounded-2xl border border-brand-primary bg-brand-primary px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-primaryDark"
                onClick={handleAddGoal}
              >
                <IconPlus className="h-3.5 w-3.5" /> Add goal
              </button>
            </div>

            {goals.length === 0 && (
              <div className="mt-8 flex flex-col items-center gap-3 rounded-2xl border border-dashed border-black/10 bg-white py-12 text-center">
                <span className="text-4xl">🎯</span>
                <p className="font-medium text-text-wizard">No goals yet</p>
                <p className="text-sm text-black/40">Click &ldquo;Add goal&rdquo; to start building your plan.</p>
              </div>
            )}

            <div className="mt-4 flex flex-col gap-5">
              {goals.map((g, gIdx) => {
                const complete = isGoalComplete(g);
                return (
                  <div key={gIdx} className="rounded-xl border border-black/[0.08] bg-white p-4">
                    {/* Goal header */}
                    <div className="flex items-start gap-3">
                      <button
                        type="button"
                        className="mt-0.5 shrink-0 text-brand-primary"
                        onClick={() => {
                          const allDone = complete;
                          setPlan((prev) => {
                            const gs = (prev.goals || []).slice();
                            const ms = (gs[gIdx]?.milestones || []).map((m) => ({ ...m, done: !allDone }));
                            gs[gIdx] = { ...gs[gIdx], milestones: ms };
                            return { ...prev, goals: gs };
                          });
                        }}
                      >
                        {complete ? (
                          <IconCheckFilled className="h-5 w-5 text-brand-primary" />
                        ) : (
                          <IconCircle className="h-5 w-5 text-black/20" />
                        )}
                      </button>
                      <input
                        className="flex-1 bg-transparent text-base font-bold text-text-wizard outline-none"
                        value={g.title || ""}
                        onChange={(e) => handleGoalTitleChange(gIdx, e.target.value)}
                        placeholder="Goal title"
                      />
                      <button
                        type="button"
                        className="ml-1 shrink-0 text-black/25 transition-colors hover:text-red-400"
                        onClick={() => handleRemoveGoal(gIdx)}
                        title="Remove goal"
                      >
                        <IconTrash className="h-4 w-4" />
                      </button>
                    </div>

                    {/* Milestones */}
                    <div className="mt-3 pl-8">
                      <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-black/35">Milestones</p>
                      <div className="flex flex-col gap-1.5">
                        {(g.milestones || []).map((m, mIdx) => {
                          const isEditing = editing.g === gIdx && editing.m === mIdx;
                          return (
                            <div
                              key={mIdx}
                              className="group flex items-center gap-2 rounded-lg px-3 py-2 transition-colors"
                              style={{ background: m.done ? "#e0f2fe" : "#f1f5f9" }}
                            >
                              <button
                                type="button"
                                className="shrink-0 text-xs"
                                onClick={() => toggleMilestone(gIdx, mIdx)}
                                title={m.done ? "Mark undone" : "Mark done"}
                              >
                                {m.done ? "✓" : "○"}
                              </button>
                              {isEditing ? (
                                <input
                                  autoFocus
                                  className="flex-1 bg-transparent text-sm text-text-wizard outline-none"
                                  value={m.title}
                                  onChange={(e) => handleMilestoneTitleChange(gIdx, mIdx, e.target.value)}
                                  onBlur={() => {
                                    if (!m.title.trim()) handleRemoveMilestone(gIdx, mIdx);
                                    else setEditing({ g: null, m: null });
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      if (!m.title.trim()) handleRemoveMilestone(gIdx, mIdx);
                                      else setEditing({ g: null, m: null });
                                    }
                                    if (e.key === "Escape") {
                                      if (!m.title.trim()) handleRemoveMilestone(gIdx, mIdx);
                                      else setEditing({ g: null, m: null });
                                    }
                                  }}
                                />
                              ) : (
                                <span
                                  className="flex-1 cursor-text text-sm text-text-wizard"
                                  onClick={() => setEditing({ g: gIdx, m: mIdx })}
                                >
                                  {m.title || <span className="text-black/30">Untitled milestone</span>}
                                </span>
                              )}
                              <button
                                type="button"
                                className="hidden shrink-0 text-black/25 transition-colors hover:text-red-400 group-hover:inline-flex"
                                onClick={() => handleRemoveMilestone(gIdx, mIdx)}
                              >
                                <IconTrash className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                      <button
                        type="button"
                        className="mt-2 text-xs font-medium text-brand-primary hover:underline"
                        onClick={() => handleAddMilestone(gIdx)}
                      >
                        + Add milestone
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-8 flex items-center gap-3">
              <button type="button" className={btnBack} onClick={handleWizardBack}>
                Back
              </button>
              <button
                type="button"
                className={btnPrimary}
                onClick={handleWizardNext}
                data-testid="dream-wizard-next-button"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </DreamWizardBleed>
    );
  }

  /* ── Step 1 – Timeline ──────────────────────────────────────────────── */

  if (step === 1) {
    const FULL_MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
    const SHORT_MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    const yearRange = Array.from({ length: 10 }, (_, i) => nowDate.year - 2 + i);
    const nextMonthNum = nowDate.month === 12 ? 1 : nowDate.month + 1;
    const getMonthLabel = (m: number) => {
      if (m === nowDate.month) return "This Month";
      if (m === nextMonthNum) return "Next Month";
      return FULL_MONTHS[m - 1];
    };

    const pickerBtnCls = (selected: boolean) =>
      cn(
        "w-[120px] cursor-pointer whitespace-nowrap rounded-lg border px-3.5 py-1.5 text-left text-xs transition-colors",
        selected
          ? "border-brand-primary bg-brand-overlay font-semibold text-brand-primary"
          : "border-black/[0.08] bg-white font-medium text-text-wizard hover:border-brand-primary/40",
      );

    const MonthYearPicker = ({ value, onChangeMonth, onChangeYear }: {
      value: { month: number; year: number };
      onChangeMonth: (m: number) => void;
      onChangeYear: (y: number) => void;
    }) => (
      <div className="flex gap-2">
        {/* Month column */}
        <div className="flex flex-col gap-1.5">
          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
            <button key={m} type="button" className={pickerBtnCls(value.month === m)} onClick={() => onChangeMonth(m)}>
              {getMonthLabel(m)}
            </button>
          ))}
          <span className="pl-1 text-lg text-slate-400">…</span>
        </div>
        {/* Year column */}
        <div className="flex flex-col gap-1.5">
          {yearRange.map((y) => (
            <button key={y} type="button" className={pickerBtnCls(value.year === y)} onClick={() => onChangeYear(y)}>
              {y === nowDate.year ? "Current year" : String(y)}
            </button>
          ))}
        </div>
      </div>
    );

    return (
      <DreamWizardBleed>
        {toastEl}
        <div
          data-testid="dream-wizard-step-1"
          className="box-border overflow-y-auto py-6"
          style={{ minHeight: "calc(100svh - 186px)" }}
        >
          <div className="mx-auto max-w-[1100px] px-4">
            {/* Dream summary pill */}
            <div className="mb-6 rounded-xl border border-slate-200 bg-white px-4 py-3">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-text-wizard">{formattedDreamTitle}</span>
                {category && (
                  <span
                    className="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white"
                    style={{ background: dreamColor }}
                  >
                    {category}
                  </span>
                )}
              </div>
              {description?.trim() && (
                <p className="mt-1 truncate text-xs text-slate-400">{description.trim()}</p>
              )}
            </div>

            {/* Timeline row — circles + connecting line */}
            <div className="flex flex-col items-center gap-3 sm:flex-row">
              <div className="flex h-24 w-24 shrink-0 flex-col items-center justify-center rounded-full bg-brand-primary shadow-lg sm:h-28 sm:w-28">
                <span className="text-[10px] font-bold uppercase tracking-widest text-white/60">Start</span>
                <span className="mt-0.5 text-[11px] font-bold text-white">{FULL_MONTHS[startMY.month - 1]}</span>
                <span className="text-sm font-bold text-white">{startMY.year}</span>
              </div>
              <div className="h-8 w-1.5 bg-slate-200 sm:h-1.5 sm:flex-1 sm:w-auto" />
              <div className="flex h-24 w-24 shrink-0 flex-col items-center justify-center rounded-full bg-brand-primary shadow-lg sm:h-28 sm:w-28">
                <span className="text-[10px] font-bold uppercase tracking-widest text-white/60">End</span>
                <span className="mt-0.5 text-[11px] font-bold text-white">{FULL_MONTHS[targetMY.month - 1]}</span>
                <span className="text-sm font-bold text-white">{targetMY.year}</span>
              </div>
            </div>

            {/* Timeline auto-fill hint */}
            {timelineHint && (
              <p style={{ fontSize: 12, color: "#22c55e", display: "flex", alignItems: "center", gap: 5, marginTop: 4 }}>
                <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor"><path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm3.28 5.78-4 4a.75.75 0 0 1-1.06 0l-1.5-1.5a.75.75 0 1 1 1.06-1.06l.97.97 3.47-3.47a.75.75 0 0 1 1.06 1.06z"/></svg>
                Dates auto-filled based on your dream — adjust if needed
              </p>
            )}

            {/* Month/Year pickers — below circles */}
            <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:justify-between">
              <MonthYearPicker
                value={startMY}
                onChangeMonth={(m) => { setStartPreset("custom"); setStartCustom((p) => ({ ...p, month: m })); }}
                onChangeYear={(y) => { setStartPreset("custom"); setStartCustom((p) => ({ ...p, year: y })); }}
              />

              <div className="flex items-start pt-2">
                {!isDateOrderValid && (
                  <p className="flex items-center gap-1 text-xs font-medium text-red-500">
                    <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm0 3.5a.75.75 0 0 1 .75.75v3a.75.75 0 0 1-1.5 0v-3A.75.75 0 0 1 8 4.5zm0 7a.875.875 0 1 1 0-1.75.875.875 0 0 1 0 1.75z" />
                    </svg>
                    Target must be on or after start
                  </p>
                )}
              </div>

              <MonthYearPicker
                value={targetMY}
                onChangeMonth={(m) => { setTargetPreset("custom"); setTargetCustom((p) => ({ ...p, month: m })); }}
                onChangeYear={(y) => { setTargetPreset("custom"); setTargetCustom((p) => ({ ...p, year: y })); }}
              />
            </div>

            {/* Back / Next */}
            <div className="mt-8 flex items-center gap-3">
              <button type="button" className={btnBack} onClick={handleWizardBack}>Back</button>
              <button
                type="button"
                className={btnPrimary}
                onClick={handleWizardNext}
                disabled={!isDateOrderValid || isSaving}
                data-testid="dream-wizard-next-button"
              >
                {isSaving ? "Creating…" : pendingPayload ? "Retry Submitted Save" : mode === "manual" ? "Create Dream" : mode === "vendor" ? "Create Dream" : "Next"}
              </button>
            </div>
          </div>
        </div>
      </DreamWizardBleed>
    );
  }

  /* ── Step 2 – Generating plan (motivational loading) ──────────────── */

  if (step === 2) {
    const quote = MOTIVATIONAL_QUOTES[quoteIdx];
    const planReady = !isGenerating && draft;

    return (
      <DreamWizardBleed>
        {toastEl}
        <div
          data-testid="dream-wizard-step-2"
          className="box-border flex min-h-[calc(100svh-186px)] flex-col items-center justify-center overflow-y-auto py-12"
        >
          <div className="flex w-full max-w-[480px] flex-col items-center text-center">

            {/* Animated pulsing icon */}
            {!planReady ? (
              <div className="relative mb-8">
                <div className="absolute inset-0 animate-ping rounded-full bg-brand-primary/20" style={{ animationDuration: "2s" }} />
                <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-brand-primary to-[#3ED8D8]">
                  <span className="text-3xl">✨</span>
                </div>
              </div>
            ) : (
              <div className="mb-8 flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
                <span className="text-3xl">🎉</span>
              </div>
            )}

            {/* Status text */}
            <h2 className="mb-2 text-xl font-bold text-text-wizard">
              {planReady
                ? "Your dream plan is ready!"
                : "Crafting your personalised plan…"}
            </h2>
            <p className="mb-8 text-sm text-black/50">
              {planReady
                ? "Saving your dream…"
                : `Planning "${formattedDreamTitle}" with goals & milestones tailored just for you.`}
            </p>

            {createError && <div role="alert" className="mb-6 space-y-3 text-sm text-red-700">
              <p>{pendingPayload ? "Retry will finish the original submitted plan. Make further edits after it is saved." : createError}</p>
              <button type="button" className={btnPrimary} disabled={isSaving} onClick={handleWizardNext} data-testid="dream-retry-save">Retry Save</button>
            </div>}

            {/* Motivational quote with fade transition */}
            {!planReady ? (
              <div className="min-h-[80px] w-full">
                <div
                  className="rounded-xl bg-white/80 px-6 py-5 shadow-sm transition-opacity duration-400"
                  style={{ opacity: quoteVisible ? 1 : 0 }}
                >
                  <p className="text-base italic leading-relaxed text-text-wizard">
                    "{quote.text}"
                  </p>
                  {quote.author ? (
                    <p className="mt-2 text-xs font-medium text-black/40">
                      — {quote.author}
                    </p>
                  ) : null}
                </div>
              </div>
            ) : null}

            {/* Progress dots animation */}
            {!planReady ? (
              <div className="mt-6 flex items-center gap-1.5">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="h-2 w-2 rounded-full bg-brand-primary/60"
                    style={{
                      animation: "pulse 1.4s ease-in-out infinite",
                      animationDelay: `${i * 0.2}s`,
                    }}
                  />
                ))}
              </div>
            ) : (
              <div className="mt-2 flex items-center gap-2">
                <svg className="h-4 w-4 animate-spin text-brand-primary" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
                <span className="text-sm font-medium text-brand-primary">Saving…</span>
              </div>
            )}

          </div>
        </div>
      </DreamWizardBleed>
    );
  }

  /* ── Step 3+ is no longer used – creation happens automatically ───── */

  if (step === 3) {
    const goals = plan.goals || [];
    return (
      <DreamWizardBleed>
        {toastEl}
        <div
          data-testid="dream-wizard-step-3"
          className="box-border min-h-[calc(100svh-186px)] overflow-y-auto pt-6 pb-12"
        >
          <div className="max-w-[720px]">
            <h2 className="mb-2 text-2xl font-bold text-text-wizard">
              {formattedDreamTitle}
            </h2>
            {description?.trim() ? (
              <p className="mb-3.5 text-base text-text-wizard">{description.trim()}</p>
            ) : null}

            <div className="mt-2.5">
              <p className="text-sm text-black/45">
                You may be further along than you think
              </p>
              <p className="text-sm text-black/45">
                Check a goal when all its milestones feel done, then use the pencil and bin on each row to refine the plan.
              </p>
            </div>

            <div className="mt-3.5 flex items-center justify-between">
              <div>
                <p className="text-sm font-bold text-text-wizard">
                  {`${goals.length} Goals created`}
                </p>
                <p className="text-xs text-black/45">
                  These are suggestions. You can remove or edit anything.
                </p>
              </div>

              <button
                type="button"
                className="inline-flex cursor-pointer items-center gap-1.5 rounded-2xl border border-brand-primary bg-brand-primary px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-primaryDark"
                onClick={handleAddGoal}
              >
                <IconPlus className="h-3.5 w-3.5" />
                Add goal
              </button>
            </div>

            <div className="mt-4.5 flex flex-col gap-6">
              {goals.map((g, gIdx) => (
                <div key={gIdx} className="rounded-xl border border-black/[0.08] bg-white p-4">
                  <div className="inline-flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleToggleGoalComplete(gIdx)}
                      className="inline-flex cursor-pointer items-center gap-2.5 border-0 bg-transparent p-0"
                      aria-label={isGoalComplete(g) ? "Mark goal incomplete" : "Mark goal complete"}
                    >
                      {isGoalComplete(g) ? (
                        <IconCheckFilled className="text-brand-primary" />
                      ) : (
                        <IconCircle className="text-black/25" />
                      )}
                      <span className="text-[15px] font-semibold text-text-wizard">
                        {g.title || "Goal"}
                      </span>
                    </button>

                    <button
                      type="button"
                      aria-label="Remove goal"
                      onClick={() => handleRemoveGoal(gIdx)}
                      className="inline-flex cursor-pointer items-center justify-center border-0 bg-transparent p-0"
                    >
                      <IconTrash className="text-black/45" />
                    </button>
                  </div>

                  <p className="mt-3 text-[11px] font-bold tracking-wide text-black/45">
                    MILESTONES
                  </p>

                  <div className="mt-2.5 flex flex-col gap-2">
                    {(g.milestones || []).map((m, mIdx) => {
                      const isEditing = editing.g === gIdx && editing.m === mIdx;
                      if (isEditing) {
                        return (
                          <input
                            key={mIdx}
                            autoFocus
                            type="text"
                            value={m.title}
                            onChange={(e) =>
                              handleMilestoneTitleChange(gIdx, mIdx, e.target.value)
                            }
                            onBlur={() => {
                              setEditing({ g: null, m: null });
                              if (!(m.title || "").trim()) handleRemoveMilestone(gIdx, mIdx);
                            }}
                            onKeyDown={(e) => {
                              if (e.key !== "Enter") return;
                              setEditing({ g: null, m: null });
                              if (!(m.title || "").trim()) handleRemoveMilestone(gIdx, mIdx);
                            }}
                            placeholder="Write your milestone..."
                            className="w-full rounded border border-black/[0.15] px-3 py-2 text-sm text-text-wizard outline-none focus:border-brand-primary"
                          />
                        );
                      }
                      return (
                        <button
                          key={mIdx}
                          type="button"
                          className={milestonePillCls(!!m.done)}
                          onClick={() => toggleMilestone(gIdx, mIdx)}
                        >
                          <span className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap">
                            {(m.title || "").trim()}
                          </span>

                          <span
                            role="button"
                            tabIndex={0}
                            aria-label="Edit milestone"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setEditing({ g: gIdx, m: mIdx });
                            }}
                            onKeyDown={(e) => {
                              if (e.key !== "Enter" && e.key !== " ") return;
                              e.preventDefault();
                              e.stopPropagation();
                              setEditing({ g: gIdx, m: mIdx });
                            }}
                            className="inline-flex items-center justify-center pl-1.5"
                          >
                            <IconEdit className="text-black/45" />
                          </span>

                          <span
                            role="button"
                            tabIndex={0}
                            aria-label="Remove milestone"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleRemoveMilestone(gIdx, mIdx);
                            }}
                            onKeyDown={(e) => {
                              if (e.key !== "Enter" && e.key !== " ") return;
                              e.preventDefault();
                              e.stopPropagation();
                              handleRemoveMilestone(gIdx, mIdx);
                            }}
                            className="inline-flex items-center justify-center pl-1.5"
                          >
                            <IconTrash className="text-black/45" />
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  <div className="mt-2.5 flex justify-start">
                    <button
                      type="button"
                      className="cursor-pointer rounded-2xl border border-brand-primary bg-white px-5 py-1.5 text-xs font-medium text-brand-primary transition-colors hover:bg-blue-50"
                      onClick={() => handleAddMilestone(gIdx)}
                    >
                      Add
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 flex items-center gap-3">
              <button type="button" className={btnBack} onClick={handleWizardBack}>
                Back
              </button>
              <button
                type="button"
                className={btnPrimary}
                onClick={handleWizardNext}
                data-testid="dream-wizard-next-button"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </DreamWizardBleed>
    );
  }

  /* ── Fallthrough – should not normally render ─────────────────────── */

  const progressPercent = completedGoalsCount.total
    ? Math.round((completedGoalsCount.done / completedGoalsCount.total) * 100)
    : 0;

  return (
    <DreamWizardBleed>
      {toastEl}
      <div
        data-testid="dream-wizard-step-5"
        className="box-border min-h-[calc(100svh-186px)] overflow-y-auto pt-6 pb-12"
      >
        <div className="max-w-[980px]">
          <div className="flex flex-col gap-8 md:flex-row md:items-start">
            <div className="min-w-[320px] flex-1">
              <span className="text-[11px] font-bold uppercase tracking-widest text-[#17A6B3]">
                {"I'M DREAMING TO"}
              </span>
              <h2 className="mt-2 text-2xl font-bold text-text-wizard">
                {formattedDreamTitle}
              </h2>
              {description?.trim() ? (
                <p className="mt-1 text-sm text-text-wizard">{description.trim()}</p>
              ) : null}
            </div>

            <div className="w-full rounded-xl border border-black/10 bg-white p-[18px] md:w-[420px]">
              <h4 className="mt-0 text-lg font-bold text-text-wizard">Plan</h4>

              <div className="mt-1.5">
                <p className="font-bold text-text-wizard">
                  {"You've taken the first steps toward this"}
                </p>
                <p className="mt-0.5 text-sm text-black/45">
                  {`Keep going — you've completed ${completedGoalsCount.done} of ${completedGoalsCount.total} goals.`}
                </p>
              </div>

              <div className="mt-2.5">
                <ProgressBar percent={progressPercent} />
              </div>

              <div className="mt-3.5">
                <p className="font-bold text-text-wizard">Goals</p>
                <div className="mt-2 flex flex-col gap-2">
                  {(plan.goals || []).map((g, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <span className="text-brand-primary">
                        {isGoalComplete(g) ? "✓" : "○"}
                      </span>
                      <span className="text-sm text-text-wizard">{g.title || "Goal"}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-3.5">
                <p className="font-bold text-text-wizard">Milestones</p>
                <div className="mt-2 flex flex-col gap-2">
                  {(plan.goals || [])
                    .flatMap((g) => g.milestones || [])
                    .slice(0, 6)
                    .map((m, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <span className="text-brand-primary">▦</span>
                        <span className="text-sm text-text-wizard">
                          {m.title || "Milestone"}
                        </span>
                        {m.date ? (
                          <span className="text-sm text-black/45">— {m.date}</span>
                        ) : null}
                      </div>
                    ))}
                </div>
              </div>

              <div className="mt-4.5 flex justify-end">
                <button
                  type="button"
                  className={btnPrimary}
                  disabled={isCreating}
                  onClick={handleCreateDream}
                >
                  {isCreating ? "Creating…" : "Yes, let's do this"}
                </button>
              </div>
            </div>
          </div>

          <div className="mt-5.5">
            <button
              type="button"
              className={btnBack}
              onClick={handleWizardBack}
              disabled={isCreating}
            >
              Back
            </button>
          </div>
        </div>
      </div>
      <UpgradeModal
        open={showUpgradeGate}
        trigger="dream_limit"
        onClose={() => setShowUpgradeGate(false)}
      />
    </DreamWizardBleed>
  );
}
